import { STORAGE_KEYS, SESSION_CONFIG, ALLOWED_GOOGLE_DOMAIN } from '../constants';
import type { GoogleTokenResponse, IdTokenClaims, GoogleUser } from '../types/auth';

const CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID;
const REDIRECT_URI = `${window.location.origin}/callback`;

// PKCE helpers

async function generateCodeVerifier(): Promise<string> {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return btoa(String.fromCharCode(...array))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
}

async function generateCodeChallenge(verifier: string): Promise<string> {
  const data = new TextEncoder().encode(verifier);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return btoa(String.fromCharCode(...new Uint8Array(digest)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
}

function decodeIdToken(idToken: string): IdTokenClaims {
  const payload = idToken.split('.')[1];
  const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
  return JSON.parse(decoded);
}

// OAuth flow

export async function initiateLogin(): Promise<void> {
  if (!CLIENT_ID) {
    throw new Error('VITE_GOOGLE_CLIENT_ID is not set');
  }

  const codeVerifier = await generateCodeVerifier();
  const codeChallenge = await generateCodeChallenge(codeVerifier);
  const state = crypto.randomUUID();

  sessionStorage.setItem(STORAGE_KEYS.CODE_VERIFIER, codeVerifier);
  sessionStorage.setItem(STORAGE_KEYS.OAUTH_STATE, state);

  const params = new URLSearchParams({
    response_type: 'code',
    client_id: CLIENT_ID,
    redirect_uri: REDIRECT_URI,
    scope: 'openid email profile',
    code_challenge: codeChallenge,
    code_challenge_method: 'S256',
    state,
    hd: ALLOWED_GOOGLE_DOMAIN,
    access_type: 'offline',
    prompt: 'select_account',
  });

  window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
}

export async function handleCallback(): Promise<boolean> {
  const urlParams = new URLSearchParams(window.location.search);
  const code = urlParams.get('code');
  const state = urlParams.get('state');
  const error = urlParams.get('error');

  if (error) {
    console.error('OAuth error:', error, urlParams.get('error_description'));
    return false;
  }

  const storedState = sessionStorage.getItem(STORAGE_KEYS.OAUTH_STATE);
  if (state !== storedState) {
    console.error('OAuth state mismatch');
    return false;
  }

  if (!code) {
    console.error('No authorization code received');
    return false;
  }

  const codeVerifier = sessionStorage.getItem(STORAGE_KEYS.CODE_VERIFIER);
  if (!codeVerifier) {
    console.error('Code verifier not found');
    return false;
  }

  try {
    const response = await fetch('/api/token', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        code,
        redirect_uri: REDIRECT_URI,
        client_id: CLIENT_ID!,
        code_verifier: codeVerifier,
      }),
    });

    if (!response.ok) {
      throw new Error(`Token exchange failed: ${response.status}`);
    }

    const tokens: GoogleTokenResponse = await response.json();

    if (!tokens.id_token) {
      console.error('No ID token in response');
      return false;
    }

    const claims = decodeIdToken(tokens.id_token);

    // Domain restriction: only allow encorestateplans.com accounts
    if (claims.hd !== ALLOWED_GOOGLE_DOMAIN) {
      console.error('Unauthorized domain:', claims.hd);
      return false;
    }

    localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, tokens.access_token);
    localStorage.setItem(STORAGE_KEYS.ID_TOKEN, tokens.id_token);
    localStorage.setItem(STORAGE_KEYS.USER_NAME, claims.name);
    localStorage.setItem(STORAGE_KEYS.USER_EMAIL, claims.email);
    if (claims.picture) {
      localStorage.setItem(STORAGE_KEYS.USER_PICTURE, claims.picture);
    }
    localStorage.setItem(STORAGE_KEYS.SESSION_START, Date.now().toString());

    sessionStorage.removeItem(STORAGE_KEYS.CODE_VERIFIER);
    sessionStorage.removeItem(STORAGE_KEYS.OAUTH_STATE);

    return true;
  } catch (error) {
    console.error('OAuth callback error:', error);
    return false;
  }
}

// Token management

export function getStoredToken(): string | null {
  return localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
}

export function getStoredUser(): GoogleUser | null {
  const name = localStorage.getItem(STORAGE_KEYS.USER_NAME);
  const email = localStorage.getItem(STORAGE_KEYS.USER_EMAIL);
  if (!name || !email) return null;
  return {
    name,
    email,
    picture: localStorage.getItem(STORAGE_KEYS.USER_PICTURE) || undefined,
  };
}

export function clearTokens(): void {
  localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
  localStorage.removeItem(STORAGE_KEYS.ID_TOKEN);
  localStorage.removeItem(STORAGE_KEYS.USER_NAME);
  localStorage.removeItem(STORAGE_KEYS.USER_EMAIL);
  localStorage.removeItem(STORAGE_KEYS.USER_PICTURE);
  localStorage.removeItem(STORAGE_KEYS.SESSION_START);
}

// Session management

export function getSessionStart(): number | null {
  const stored = localStorage.getItem(STORAGE_KEYS.SESSION_START);
  if (!stored) return null;
  const ts = parseInt(stored, 10);
  return isNaN(ts) ? null : ts;
}

export function isSessionExpired(): boolean {
  const start = getSessionStart();
  if (!start) return true;
  return Date.now() - start >= SESSION_CONFIG.MAX_DURATION_MS;
}

export function isSessionExpiringSoon(): boolean {
  const start = getSessionStart();
  if (!start) return false;
  const remaining = SESSION_CONFIG.MAX_DURATION_MS - (Date.now() - start);
  return remaining > 0 && remaining <= SESSION_CONFIG.WARNING_THRESHOLD_MS;
}

export function getSessionTimeRemaining(): number {
  const start = getSessionStart();
  if (!start) return 0;
  return Math.max(0, SESSION_CONFIG.MAX_DURATION_MS - (Date.now() - start));
}

export function extendSession(): void {
  localStorage.setItem(STORAGE_KEYS.SESSION_START, Date.now().toString());
}
